import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import AIAgentChatInterfaceDesktop from '@/components/ai-agents/AIAgentChatInterfaceDesktop';
import ThreadsListDesktop from '@/components/ai-agents/threads/ThreadsListDesktop';
import { useUser } from '@/hooks/auth/use-user';
import { useAIAgentSidebarState } from '@/hooks/ai-agents/useAIAgentSidebarState';

/**
 * Unified AI Agent Sidebar Content - Clean Architecture
 * 
 * ✅ FIXED: Now handles input without localStorage persistence during typing
 * 
 * This component gets state from the centralized useAIAgentSidebarState hook,
 * but prevents localStorage writes during active typing to avoid conflicts.
 * 
 * Key improvements:
 * - No localStorage persistence during typing
 * - localStorage only used for prompt application and clear on send
 * - Eliminates old message replacement issues
 * - Cleaner component interface
 */
export const AIAgentSidebarContent: React.FC<{ onClose?: () => void }> = ({ onClose }) => {
  const { user } = useUser();
  
  // ✅ CENTRALIZED STATE: Get all state from our clean architecture
  const { 
    activeTab, 
    inputValue, 
    handleApplyPrompt, 
    switchToChatTab,
    handleInputChange
  } = useAIAgentSidebarState();

  // Simple fade animation variants
  const fadeVariants = {
    initial: { opacity: 0, y: 10 },
    animate: { opacity: 1, y: 0 },
    exit: { opacity: 0, y: -10 }
  };

  return (
    <div className="flex-1 flex flex-col min-h-0 overflow-hidden">
      <AnimatePresence mode="wait">
        {activeTab === 'chat' && (
          <motion.div 
            key="chat"
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
            className="flex-1 flex flex-col min-h-0"
          >
            <AIAgentChatInterfaceDesktop 
              inputValue={inputValue} 
              onInputChange={handleInputChange}
              onClose={onClose}
            />
          </motion.div>
        )}
        
        {activeTab === 'threads' && (
          <motion.div 
            key="threads"
            variants={fadeVariants}
            initial="initial"
            animate="animate"
            exit="exit"
            transition={{ duration: 0.2 }}
            className="flex-1 flex flex-col min-h-0"
          >
            {/* ✅ Pass the unified switchToChatTab function directly - no more prop drilling! */}
            <ThreadsListDesktop onThreadSelect={switchToChatTab} />
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}; 